import { useTheme } from '../../../hooks/useTheme'
import styles from './GitHubWidget.module.css'

/* ─────────────────────────────────────────────────
   Types
───────────────────────────────────────────────── */

export type GitHubWidgetType = 'streak' | 'contributions' | 'overview' | 'languages'

interface GitHubWidgetProps {
  /** Type de widget GitHub à afficher */
  type: GitHubWidgetType
  /** Nom d'utilisateur GitHub */
  username: string
}

/* ─────────────────────────────────────────────────
   Titres affichés par type de widget
───────────────────────────────────────────────── */
const WIDGET_TITLES: Record<GitHubWidgetType, string> = {
  streak:        'Streak',
  contributions: `${new Date().getFullYear()} Contributions`,
  overview:      'Overview',
  languages:     'Top Languages',
}

/* ─────────────────────────────────────────────────
   Génération des URLs selon le thème courant
───────────────────────────────────────────────── */
function buildWidgetUrl(
  type: GitHubWidgetType,
  username: string,
  isDark: boolean,
): string {
  /*
   * Thèmes utilisés :
   *   dark  → "github-dark-blue" (proche palette .NET sombre)
   *   light → "react"            (compatible fond clair)
   */
  const theme = isDark ? 'material-palenight' : 'modern-lilac2'
  const contribColor = isDark ? '512BD4' : '68217A'

  switch (type) {
    case 'streak':
      return `https://github-readme-streak-stats-teal-theta.vercel.app?user=${username}&theme=modern-lilac2`

    case 'contributions':
      return `https://ghchart.rshah.org/${contribColor}/${username}`

    case 'overview':
      return `https://github-readme-stats-fast.vercel.app/api?username=${username}&show_icons=true&theme=${theme}&hide_title=true`

    case 'languages':
      return `https://github-readme-stats-fast.vercel.app/api/top-langs/?username=${username}&layout=compact&langs_count=8&theme=${theme}`
  }
}

/* ─────────────────────────────────────────────────
   Composant
───────────────────────────────────────────────── */
export function GitHubWidget({ type, username }: GitHubWidgetProps) {
  const { isDark } = useTheme()

  const src   = buildWidgetUrl(type, username, isDark)
  const title = WIDGET_TITLES[type]
  const alt   = `Widget GitHub — ${title} de ${username}`

  return (
    <div className={styles.card} aria-label={alt}>
      {/* Titre de la card */}
      <h3 className={styles.title}>{title}</h3>

      {/* Conteneur image avec skeleton pendant le chargement */}
      <div className={styles.imageWrapper}>
        <img
          /*
           * La clé sur `src` force React à recréer l'<img>
           * quand le thème change, évitant un flash de l'ancienne image.
           * Note : on utilise `key` via le parent (GitHubSection),
           * mais on laisse `src` changer directement ici — le navigateur
           * gère le remplacement sans flash visible.
           */
          src={src}
          alt={alt}
          className={styles.image}
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  )
}